var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { Allow, IsIn, IsInt, IsNotEmpty, IsUUID, Max, Min } from "class-validator";
import { plainToInstance, Transform } from "class-transformer";
import { roundToPrecision } from "../../utils";
import { BaseDTO } from "../public";
import { ExposeFrontend } from "../../decorators";
import { ReviewDTO } from "./Reviews";
import { DEPARTMENT_LIST } from "../../constants";
export class TruncatedProfessorDTO extends BaseDTO {
}
__decorate([
    IsUUID(),
    ExposeFrontend()
], TruncatedProfessorDTO.prototype, "id", void 0);
__decorate([
    IsIn(DEPARTMENT_LIST),
    ExposeFrontend()
], TruncatedProfessorDTO.prototype, "department", void 0);
__decorate([
    IsNotEmpty(),
    ExposeFrontend()
], TruncatedProfessorDTO.prototype, "firstName", void 0);
__decorate([
    IsNotEmpty(),
    ExposeFrontend()
], TruncatedProfessorDTO.prototype, "lastName", void 0);
__decorate([
    IsInt(),
    Min(0),
    ExposeFrontend()
], TruncatedProfessorDTO.prototype, "numEvals", void 0);
__decorate([
    Min(0),
    Max(4),
    ExposeFrontend()
], TruncatedProfessorDTO.prototype, "overallRating", void 0);
__decorate([
    Min(0),
    Max(4),
    ExposeFrontend()
], TruncatedProfessorDTO.prototype, "materialClear", void 0);
__decorate([
    Min(0),
    Max(4),
    ExposeFrontend()
], TruncatedProfessorDTO.prototype, "studentDifficulties", void 0);
__decorate([
    Allow(),
    ExposeFrontend()
], TruncatedProfessorDTO.prototype, "courses", void 0);
export class ProfessorDTO extends TruncatedProfessorDTO {
    addReview(review, courseName) {
        review.professor = this.id;
        if (!this.courses.includes(courseName)) {
            this.courses.push(courseName);
        }
        const reviews = this.reviews[courseName];
        if (!reviews) {
            this.reviews[courseName] = [review];
        }
        else {
            reviews.push(review);
        }
        const newMaterial = (this.materialClear * this.numEvals + review.presentsMaterialClearly) /
            (this.numEvals + 1);
        const newStudentDiff = (this.studentDifficulties * this.numEvals + review.recognizesStudentDifficulties) /
            (this.numEvals + 1);
        const newOverall = (this.overallRating * this.numEvals + review.overallRating) / (this.numEvals + 1);
        this.numEvals += 1;
        this.materialClear = roundToPrecision(newMaterial, 2);
        this.studentDifficulties = roundToPrecision(newStudentDiff, 2);
        this.overallRating = roundToPrecision(newOverall, 2);
    }
    removeReview(reviewId) {
        const targetCourse = Object.entries(this.reviews).find(([, courseReviews]) => courseReviews.find((review) => review.id === reviewId));
        if (!targetCourse) {
            throw new Error("Review Does not exist");
        }
        const [courseName, reviews] = targetCourse;
        let removedReview;
        if (reviews.length === 1) {
            [removedReview] = this.reviews[courseName];
            delete this.reviews[courseName];
            const coursesIndex = this.courses.indexOf(courseName);
            if (coursesIndex === -1) {
                throw new Error("Course to be removed is missing from professorDTO courses");
            }
            this.courses.splice(coursesIndex, 1);
        }
        else {
            const reviewIndex = reviews.findIndex((review) => review.id === reviewId);
            removedReview = this.reviews[courseName][reviewIndex];
            this.reviews[courseName].splice(reviewIndex, 1);
        }
        if (this.numEvals === 1) {
            this.materialClear = 0;
            this.studentDifficulties = 0;
            this.overallRating = 0;
            this.numEvals = 0;
        }
        else {
            const newMaterial = (this.materialClear * this.numEvals - removedReview.presentsMaterialClearly) /
                (this.numEvals - 1);
            const newStudentDiff = (this.studentDifficulties * this.numEvals -
                removedReview.recognizesStudentDifficulties) /
                (this.numEvals - 1);
            const newOverall = (this.overallRating * this.numEvals - removedReview.overallRating) /
                (this.numEvals - 1);
            this.numEvals -= 1;
            this.materialClear = roundToPrecision(newMaterial, 2);
            this.studentDifficulties = roundToPrecision(newStudentDiff, 2);
            this.overallRating = roundToPrecision(newOverall, 2);
        }
    }
    toTruncatedProfessorDTO() {
        return plainToInstance(TruncatedProfessorDTO, this, { excludeExtraneousValues: true });
    }
    static fromAddProfessorRequest(addProfessorRequest) {
        const newProfessorId = crypto.randomUUID();
        const plainReview = Object.assign({ professor: newProfessorId }, addProfessorRequest.review);
        const review = plainToInstance(ReviewDTO, plainReview, {
            excludeExtraneousValues: true,
        });
        const courseName = `${addProfessorRequest.review.department} ${addProfessorRequest.review.courseNum}`;
        const plain = Object.assign({ id: newProfessorId, overallRating: review.overallRating, studentDifficulties: review.recognizesStudentDifficulties, materialClear: review.presentsMaterialClearly, courses: [courseName], reviews: {
                [courseName]: [review],
            } }, addProfessorRequest);
        return plainToInstance(ProfessorDTO, plain, {
            excludeExtraneousValues: true,
        });
    }
}
__decorate([
    Allow(),
    Transform(({ value, options }) => {
        Object.entries(value).forEach(([course, reviews]) => {
            value[course] = plainToInstance(ReviewDTO, reviews, options);
        });
        return value;
    }, { toClassOnly: true }),
    ExposeFrontend()
], ProfessorDTO.prototype, "reviews", void 0);
//# sourceMappingURL=Professors.js.map